import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import DocumentForm from '../components/DocumentForm';
import { confirmDialog } from '../utils/confirm';
import { useAppContext } from '../contexts/AppContext';
import DocViewer from '../components/DocViewer';

const parseFlexDate = (val) => {
  const s = String(val || '');
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`).getTime();
  const t = new Date(s).getTime();
  return isNaN(t) ? Infinity : t;
};

// Documents are always stored with documentType forced to ALL CAPS (backend
// normalizes it for matching/filtering). Map the well-known types back to
// their proper display casing; fall back to Title Case for custom "Other" types.
const DOC_TYPE_LABELS = { RCA: 'RCA', ITP: 'ITP', CASCO: 'CASCO', ROVINIETA: 'Rovinieta', 'ROAD ASSISTANCE': 'Road Assistance' };
const prettyDocumentType = (type) => {
  if (!type) return '';
  const upper = String(type).toUpperCase();
  if (DOC_TYPE_LABELS[upper]) return DOC_TYPE_LABELS[upper];
  return String(type).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const Documents = () => {
  const { isAdmin } = useAppContext();
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const res = await apiEndpoints.documents.list();
        setDocuments(res.data);
        setFilteredDocs(res.data);
      } catch (err) {
        console.error('Failed to load documents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  useEffect(() => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    let filtered = documents;

    if (activeTab === 'all') {
      filtered = documents;
    } else if (activeTab === 'expired') {
      filtered = documents.filter((doc) => parseFlexDate(doc.expiryDate) <= thirtyDaysFromNow.getTime());
    } else if (activeTab === 'rca') {
      filtered = documents.filter((doc) => (doc.documentType || '').toUpperCase() === 'RCA');
    } else if (activeTab === 'pti') {
      filtered = documents.filter((doc) => (doc.documentType || '').toUpperCase() === 'ITP');
    } else if (activeTab === 'casco') {
      filtered = documents.filter((doc) => (doc.documentType || '').toUpperCase() === 'CASCO');
    }

    filtered = [...filtered].sort((a, b) => parseFlexDate(a.expiryDate) - parseFlexDate(b.expiryDate));

    setFilteredDocs(filtered);
  }, [activeTab, documents]);

  if (loading) return <div className="page-loading">Loading documents...</div>;

  const getNow = () => new Date();
  const parseFlexibleDate = (value) => {
    if (!value) return null;
    const s = String(value);
    // legacy dd.mm.yyyy
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };
  const getDaysLeft = (expiryDate) => {
    const expDate = parseFlexibleDate(expiryDate);
    if (!expDate) return 9999;
    return Math.ceil((expDate - getNow()) / (1000 * 60 * 60 * 24));
  };

  const getExpiryState = (doc) => {
    const daysLeft = getDaysLeft(doc.expiryDate);
    if (daysLeft < 7) return 'critical';
    if (daysLeft < 30) return 'warning';
    return 'good';
  };

  const getStatusColor = (state) => {
    if (state === 'critical') return '#ef4444';
    if (state === 'warning') return '#f59e0b';
    return '#10b981';
  };

  const getStatusLabel = (state) => {
    if (state === 'critical') return 'Critical';
    if (state === 'warning') return 'Expiring soon';
    return 'Activ';
  };

  const handleAddDocument = () => {
    setEditingDocument(null);
    setShowDocumentForm(true);
  };

  const handleSelectDocument = (document) => {
    setSelectedDocument(document);
  };

  const handleBackToDocuments = () => {
    setSelectedDocument(null);
  };

  const handleRenewDocument = (document) => {
    setEditingDocument(document);
    setShowDocumentForm(true);
  };

  const handleDownloadFile = (document) => {
    const previewUrl = getFilePreviewUrl(document);
    if (!previewUrl) { alert('No file attached to this document.'); return; }
    const a = window.document.createElement('a');
    a.href = previewUrl;
    a.download = document.fileUrl.split('/').pop() || 'document';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const handleViewFile = (document) => {
    const url = document.fileUrl;
    if (!url) {
      alert('No file attached to this document.');
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/${url.replace(/^\//, '')}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSaveDocument = async (documentData, file) => {
    try {
      await apiEndpoints.documents.create(documentData, file);
      const res = await apiEndpoints.documents.list();
      setDocuments(res.data);
      window.dispatchEvent(new CustomEvent('fleet:docs-changed'));
      setShowDocumentForm(false);
    } catch (error) {
      console.error('Error saving document:', error);
      throw error;
    }
  };

  const getFilePreviewUrl = (doc) => {
    if (!doc.fileUrl) return null;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    return `${apiUrl}/static/${doc.fileUrl}`;
  };

  const getFilePreviewUrls = (doc) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    const names = Array.isArray(doc.fileUrls) && doc.fileUrls.length ? doc.fileUrls : (doc.fileUrl ? [doc.fileUrl] : []);
    return names.map((name) => `${apiUrl}/static/${name}`);
  };

  const handleDeleteDocument = async (doc) => {
    if (!await confirmDialog('Delete this document? This cannot be undone.')) return;
    try {
      await apiEndpoints.documents.delete(doc._id);
      setSelectedDocument(null);
      const res = await apiEndpoints.documents.list();
      setDocuments(res.data);
      window.dispatchEvent(new CustomEvent('fleet:docs-changed'));
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Error deleting document.');
    }
  };

  if (selectedDocument) {
    const expiryState = getExpiryState(selectedDocument);
    const daysLeft = getDaysLeft(selectedDocument.expiryDate);
    const statusColor = getStatusColor(expiryState);
    const statusLabel = getStatusLabel(expiryState);
    const plateLabel = selectedDocument.plate || selectedDocument.vehicle || selectedDocument.registration || 'UNKNOWN';
    const issuer = selectedDocument.issuer || selectedDocument.company || '—';
    const issueDate = selectedDocument.issueDate ? parseFlexibleDate(selectedDocument.issueDate)?.toLocaleDateString() : '—';
    const expiryDate = parseFlexibleDate(selectedDocument.expiryDate)?.toLocaleDateString() || '—';

    return (
      <div>
        <button className="back-btn" onClick={handleBackToDocuments}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to documents
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{plateLabel}</span>
          </div>
          <div className="vprofile-model">{prettyDocumentType(selectedDocument.documentType) || 'Document'}</div>
          <div className="vprofile-sub">{issuer} • expires {expiryDate}</div>
        </div>

        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Document Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleRenewDocument(selectedDocument)}>Renew</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteDocument(selectedDocument)}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                <div className="info-row"><div className="info-lbl">Vehicle</div><div className="info-val">{plateLabel}</div></div>
                <div className="info-row"><div className="info-lbl">Document type</div><div className="info-val">{prettyDocumentType(selectedDocument.documentType) || '—'}</div></div>
                <div className="info-row"><div className="info-lbl">Issuer</div><div className="info-val">{issuer}</div></div>
                <div className="info-row"><div className="info-lbl">Issued on</div><div className="info-val">{issueDate}</div></div>
                <div className="info-row"><div className="info-lbl">Expires on</div><div className="info-val">{expiryDate}</div></div>
                <div className="info-row"><div className="info-lbl">Days left</div><div className="info-val" style={{ color: statusColor }}>{daysLeft < 0 ? `${Math.abs(daysLeft)} days ago` : `${daysLeft} days`}</div></div>
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Status</div>
                  <div className="info-val">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', backgroundColor: expiryState === 'critical' ? 'rgba(239, 68, 68, 0.12)' : expiryState === 'warning' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)', color: statusColor, fontWeight: 700 }}>
                      ● {statusLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="detail-right">
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleRenewDocument(selectedDocument)}>Renew Document</button>}
                {getFilePreviewUrl(selectedDocument) ? (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => setPreviewDoc({ urls: getFilePreviewUrls(selectedDocument), filenames: (selectedDocument.fileUrls?.length ? selectedDocument.fileUrls : [selectedDocument.fileUrl]) })}
                    >
                      Preview File
                    </button>
                    <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => handleDownloadFile(selectedDocument)}>Download</button>
                  </>
                ) : (
                  <button className="btn btn-secondary" disabled style={{ opacity: 0.45, cursor: 'not-allowed', width: '100%' }}>No File Attached</button>
                )}
                {isAdmin && <button className="btn btn-danger" style={{ width: '100%' }} onClick={() => handleDeleteDocument(selectedDocument)}>Delete Document</button>}
              </div>
            </div>
          </div>
        </div>

        <DocumentForm
          isOpen={showDocumentForm}
          onClose={() => setShowDocumentForm(false)}
          document={editingDocument}
          onSave={handleSaveDocument}
        />
        <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Car Documents & Acts</div>
          <div className="ph-sub">All fleet documents — RCA, ITP, Casco, Rovinieta</div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={handleAddDocument}>
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Document
          </button>
        )}
      </div>

      <div className="htabs">
        <div className={`htab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All documents
        </div>
        <div className={`htab ${activeTab === 'expired' ? 'active' : ''}`} onClick={() => setActiveTab('expired')}>
          Expired / Almost {(() => { const n = documents.filter(doc => parseFlexDate(doc.expiryDate) <= Date.now() + 30 * 24 * 60 * 60 * 1000).length; return n > 0 ? <span style={{ display: 'inline-block', marginLeft: '6px', backgroundColor: '#ef4444', color: 'white', fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px' }}>{n}</span> : null; })()}
        </div>
        <div className={`htab ${activeTab === 'rca' ? 'active' : ''}`} onClick={() => setActiveTab('rca')}>
          RCA
        </div>
        <div className={`htab ${activeTab === 'pti' ? 'active' : ''}`} onClick={() => setActiveTab('pti')}>
          ITP
        </div>
        <div className={`htab ${activeTab === 'casco' ? 'active' : ''}`} onClick={() => setActiveTab('casco')}>
          CASCO
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>VEHICLE</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>DOCUMENT TYPE</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>INSURANCE COMPANY</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>ISSUED</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>EXPIRE</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>DAYS LEFT</th>
                <th style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>STATUS</th>
                <th style={{ textAlign: 'right', padding: '12px', color: '#9ca3af', fontSize: '11px', fontWeight: '600', letterSpacing: '0.05em' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.length > 0 ? filteredDocs.map((doc) => {
                const expiryState = getExpiryState(doc);
                const daysLeft = getDaysLeft(doc.expiryDate);
                const plateLabel = doc.plate || doc.vehicle || doc.registration || 'UNKNOWN';
                const issuer = doc.issuer || doc.company || '—';
                const issueDate = doc.issueDate ? parseFlexibleDate(doc.issueDate)?.toLocaleDateString() : '—';
                const expiryDate = parseFlexibleDate(doc.expiryDate)?.toLocaleDateString() || '—';
                const statusColor = getStatusColor(expiryState);
                const statusLabel = getStatusLabel(expiryState);

                return (
                  <tr key={doc._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => handleSelectDocument(doc)}>
                    <td style={{ padding: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                        {plateLabel}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', fontWeight: '500' }}>{prettyDocumentType(doc.documentType) || 'Document'}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>{issuer}</td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{issueDate}</td>
                    <td style={{ padding: '12px', fontSize: '13px', color: statusColor, fontWeight: '600' }}>{expiryDate}</td>
                    <td style={{ padding: '12px', fontSize: '13px', fontWeight: '600', color: statusColor }}>
                      {daysLeft < 0 ? `${Math.abs(daysLeft)}z ago` : `${daysLeft}z`}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: statusColor, backgroundColor: expiryState === 'critical' ? '#fef2f2' : expiryState === 'warning' ? '#fffbeb' : '#ecfdf5' }}>
                        ● {statusLabel}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-xs" style={{ color: '#6b7280' }} onClick={() => handleSelectDocument(doc)}>
                        View
                      </button>
                      {getFilePreviewUrl(doc) ? (
                        <button
                          className="btn btn-ghost btn-xs"
                          style={{ color: '#10b981' }}
                          onClick={e => { e.stopPropagation(); setPreviewDoc({ urls: getFilePreviewUrls(doc), filenames: (doc.fileUrls?.length ? doc.fileUrls : [doc.fileUrl]) }); }}
                        >
                          Preview
                        </button>
                      ) : (
                        <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default' }}>
                          Preview
                        </span>
                      )}
                      {isAdmin && <button className="btn btn-ghost btn-xs" style={{ color: '#3b82f6' }} onClick={() => handleRenewDocument(doc)}>
                        Renewal
                      </button>}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    No documents found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DocumentForm
        isOpen={showDocumentForm}
        onClose={() => setShowDocumentForm(false)}
        document={editingDocument}
        onSave={handleSaveDocument}
      />
      <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default Documents;
